import type { DateRange } from 'owa-datetime-utils';
import {
    tryUpdateFolderIds,
    subscribeToNotifications,
} from '../utils/tryUpdateFolderIdsAndSubscribeToNotifications';
import { KnownItemTypes } from 'owa-calendar-surface-dategrid';
import type { OwaDate } from 'owa-datetime';
import { orchestrator } from 'satcheljs';
import { loadDayGrid, selectDayGridDate } from '../actions/publicActions';
import { initializeDayGrid } from 'owa-draggable-day-grid';
import {
    getScenarioStore,
    navigateToDate,
    updateSelectionItem,
    updateFolderIds,
    updateNumDaysInDayRange,
    updateDateRangeType,
} from 'owa-day-grid-core-schema';
import { convertToSchedulingSelectionItem } from 'owa-day-grid-core-utilities';
import type { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';

export const loadDayGridOrchestrator = orchestrator(
    loadDayGrid,
    async ({
        scenario,
        initialDate,
        focusTimeDateRange,
        folderIds,
        numDaysInDayRange,
        dateRangeType,
        enableDragDrop,
    }) => {
        // Explicitly update folderIds before consuming them. Otherwise the folderId for delegate calendar folder will not work.
        const updatedFolderIds = await tryUpdateFolderIds(folderIds);
        loadDayGridInternal(
            scenario,
            initialDate,
            focusTimeDateRange,
            updatedFolderIds,
            enableDragDrop,
            numDaysInDayRange,
            dateRangeType
        );
        const store = getScenarioStore(scenario);
        const lockId = store?.eventsCacheLockId;
        subscribeToNotifications(lockId, updatedFolderIds);
    }
);

export const selectDayGridDateOrchestrator = orchestrator(
    selectDayGridDate,
    ({ scenario, newDate }) => {
        const store = getScenarioStore(scenario);
        if (store) {
            navigateToDate(scenario, newDate);
        }
    }
);

function loadDayGridInternal(
    scenario: string,
    initialDate: OwaDate,
    focusTimeDateRange: DateRange | null,
    updatedFolderIds: string[],
    enableDragDrop: boolean,
    numDaysInDayRange?: number,
    dateRangeType?: DateRangeType | undefined
) {
    const store = getScenarioStore(scenario);

    const isScenarioGridInitialized = !!store;
    const selectionItem = focusTimeDateRange
        ? convertToSchedulingSelectionItem(
              { Start: focusTimeDateRange.start, End: focusTimeDateRange.end },
              KnownItemTypes.suggestedItems
          )
        : null;

    if (isScenarioGridInitialized) {
        // if scenario exists, update date range and folderId to load events
        updateNumDaysInDayRange(scenario, numDaysInDayRange);
        navigateToDate(scenario, initialDate);
        updateFolderIds(scenario, updatedFolderIds);
        if (selectionItem) {
            updateSelectionItem(scenario, selectionItem);
        }
        updateDateRangeType(scenario, dateRangeType);
    } else {
        // initialize day grid with valid folder id, so that events for that folder will be loaded
        initializeDayGrid(
            scenario,
            updatedFolderIds, // folderIds is present for Calendar Compose. In other scenarios, it is null until calendar cache is initialized. Scenarios should updateFolderIds after the calendar cache is initialized.
            initialDate,
            selectionItem, //selectionItem
            true, // showSelectionItem
            false, // disableTabbableSurfaceItems
            numDaysInDayRange,
            enableDragDrop, // enable drag and drop
            dateRangeType
        );
    }
}
